/**
 * Safe date parsing utilities to prevent "Invalid time value" errors
 * Handles empty strings, null, undefined, and invalid date formats
 */

/**
 * Safely parse a date value, returning null for invalid dates
 */
export function safeParseDate(value: unknown): Date | null {
  if (!value || value === '') return null;

  // eslint-disable-next-line no-restricted-syntax -- this IS the safe wrapper
  const date = new Date(value as string);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Check if a value represents a valid date
 */
export function isValidDateValue(value: unknown): boolean {
  if (!value || value === '') return false;

  // eslint-disable-next-line no-restricted-syntax -- this IS the safe wrapper
  const date = new Date(value as string);
  return !isNaN(date.getTime());
}

/**
 * Sanitize date field values, converting empty strings to null
 */
export function sanitizeDateField(value: string | undefined | null): string | null {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Safely format a date value, returning fallback for invalid dates
 */
export function safeFormatDate(
  value: unknown,
  formatFn: (date: Date) => string,
  fallback: string = 'N/A'
): string {
  const date = safeParseDate(value);
  if (!date) return fallback;
  
  try {
    return formatFn(date);
  } catch (_error) {
    return fallback;
  }
}

/**
 * Today, in the `YYYY-MM-DD` shape the client foundation's read contracts want
 * for their `p_as_of` argument.
 *
 * Shared rather than redefined per surface on purpose: the as-of date is part of
 * every one of those reads' query keys, so two surfaces asking the same contract
 * on the same day must produce the identical string or they fetch twice and can
 * show a client two different answers.
 */
export function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Format a date string as a human-friendly relative time (e.g., "2h ago", "3d ago").
 * Falls back to locale date string for dates older than 7 days.
 */
export function formatRelativeTime(dateStr: string): string {
  const date = safeParseDate(dateStr) ?? new Date();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
